/**
 * Bala chart SVG components — radar (Ṣaḍbala summary), stacked bar
 * (component breakdown), heatmap (Bhāva-bala) and diverging bar
 * (Iṣṭa/Kaṣṭa). Hand-rolled SVG to avoid pulling in Recharts/D3 — print
 * sharp at any zoom and zero bundle cost.
 */
import type { ShadbalaFullRow, BhavaBalaRow } from '@/api/types';

const PLANET_HI: Record<string, string> = {
  sun: 'सू', moon: 'चं', mars: 'मं', mercury: 'बु',
  jupiter: 'गु', venus: 'शु', saturn: 'श',
};
const PLANET_EN: Record<string, string> = {
  sun: 'Sun', moon: 'Moon', mars: 'Mars', mercury: 'Mer',
  jupiter: 'Jup', venus: 'Ven', saturn: 'Sat',
};

const COMPONENT_COLOURS: Record<string, string> = {
  sthana: '#92400e',
  dig: '#0f766e',
  kala: '#5b21b6',
  cheshta: '#b45309',
  naisargika: '#475569',
  drik: '#1e3a8a',
};
const COMPONENT_LABELS: Record<string, { hi: string; en: string }> = {
  sthana: { hi: 'स्थान', en: 'Sthāna' },
  dig: { hi: 'दिग्', en: 'Dig' },
  kala: { hi: 'काल', en: 'Kāla' },
  cheshta: { hi: 'चेष्टा', en: 'Ceṣṭā' },
  naisargika: { hi: 'नैसर्गिक', en: 'Nais.' },
  drik: { hi: 'दृक्', en: 'Dṛk' },
};

// ─── 1. Radar ──────────────────────────────────────────────────────────────

export function ShadbalaRadar({
  rows,
  lang,
  size = 460,
}: {
  rows: ShadbalaFullRow[];
  lang: 'hi' | 'en';
  size?: number;
}) {
  if (!rows.length) return null;
  const cx = size / 2;
  const cy = size / 2;
  const radius = Math.min(cx, cy) - 60;
  const n = rows.length;
  const maxValue = 12.0;

  const polar = (value: number, idx: number) => {
    const angle = -Math.PI / 2 + (2 * Math.PI * idx) / n;
    const r = radius * Math.min(value / maxValue, 1.0);
    return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)] as const;
  };

  const thresholdPts = rows.map((r, i) => polar(r.required_rupas, i));
  const actualPts = rows.map((r, i) => polar(r.total_rupas, i));

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full max-w-[460px]">
      {[0.25, 0.5, 0.75, 1.0].map((t, i) => (
        <circle key={i} cx={cx} cy={cy} r={radius * t} fill="none" stroke="#27272a" strokeWidth={0.5} />
      ))}
      {rows.map((r, i) => {
        const [ax, ay] = polar(maxValue, i);
        const angle = -Math.PI / 2 + (2 * Math.PI * i) / n;
        const lx = cx + (radius + 25) * Math.cos(angle);
        const ly = cy + (radius + 25) * Math.sin(angle);
        const label = lang === 'hi' ? PLANET_HI[r.planet_id] : PLANET_EN[r.planet_id];
        const colour = r.sufficient ? '#4ade80' : '#f87171';
        return (
          <g key={r.planet_id}>
            <line x1={cx} y1={cy} x2={ax} y2={ay} stroke="#3f3f46" strokeWidth={0.6} />
            <text x={lx} y={ly} fontSize={11} fontWeight={600} fill="#e5e7eb" textAnchor="middle" dominantBaseline="middle">
              {label}
            </text>
            <text x={lx} y={ly + 13} fontSize={9} fill={colour} textAnchor="middle">
              {r.total_rupas.toFixed(2)}
            </text>
          </g>
        );
      })}
      <polygon
        points={thresholdPts.map(([x, y]) => `${x},${y}`).join(' ')}
        fill="rgba(217, 119, 6, 0.10)"
        stroke="#d97706"
        strokeWidth={1}
        strokeDasharray="4 3"
      />
      <polygon
        points={actualPts.map(([x, y]) => `${x},${y}`).join(' ')}
        fill="rgba(180, 83, 9, 0.30)"
        stroke="#fbbf24"
        strokeWidth={1.8}
      />
      {rows.map((r, i) => {
        const [x, y] = polar(r.total_rupas, i);
        return (
          <circle
            key={r.planet_id}
            cx={x}
            cy={y}
            r={3.5}
            fill={r.sufficient ? '#4ade80' : '#f87171'}
            stroke="#0c0a09"
            strokeWidth={1.2}
          />
        );
      })}
    </svg>
  );
}

// ─── 2. Stacked horizontal bar ─────────────────────────────────────────────

export function ShadbalaStacked({
  rows,
  lang,
  width = 600,
  height = 320,
}: {
  rows: ShadbalaFullRow[];
  lang: 'hi' | 'en';
  width?: number;
  height?: number;
}) {
  if (!rows.length) return null;
  const labelCol = 60;
  const marginL = 20;
  const marginR = 60;
  const marginT = 30;
  const marginB = 36;
  const plotW = width - labelCol - marginL - marginR;
  const plotH = height - marginT - marginB;
  const rowH = plotH / rows.length;
  const rowGap = 6;
  const barH = rowH - rowGap;
  const maxTotal = Math.max(600, ...rows.map((r) => r.total_virupas));
  const plotX0 = marginL + labelCol;
  const components = ['sthana', 'dig', 'kala', 'cheshta', 'naisargika', 'drik'] as const;

  const segValue = (row: ShadbalaFullRow, c: string): number => {
    if (c === 'sthana') return row.sthana.total;
    if (c === 'kala') return row.kala.total;
    if (c === 'dig') return row.dig;
    if (c === 'cheshta') return row.cheshta;
    if (c === 'naisargika') return row.naisargika;
    if (c === 'drik') return row.drik;
    return 0;
  };

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full">
      {Array.from({ length: Math.floor(maxTotal / 60) + 1 }, (_, i) => {
        const tick = i * 60;
        const x = plotX0 + (tick / maxTotal) * plotW;
        return (
          <g key={tick}>
            <line x1={x} y1={marginT} x2={x} y2={marginT + plotH} stroke="#27272a" strokeWidth={0.5} />
            {tick % 120 === 0 && (
              <text x={x} y={marginT + plotH + 14} fontSize={8} fill="#71717a" textAnchor="middle">
                {tick / 60}R
              </text>
            )}
          </g>
        );
      })}
      {rows.map((row, i) => {
        const y = marginT + i * rowH + rowGap / 2;
        const cyText = y + barH / 2;
        const label = lang === 'hi' ? PLANET_HI[row.planet_id] : PLANET_EN[row.planet_id];
        const thresholdV = row.required_rupas * 60;
        const tx = plotX0 + (thresholdV / maxTotal) * plotW;
        let xCursor = plotX0;
        const segments = components.map((c) => {
          const v = segValue(row, c);
          const segW = (v / maxTotal) * plotW;
          const seg = (
            <rect
              key={c}
              x={xCursor}
              y={y}
              width={segW}
              height={barH}
              fill={COMPONENT_COLOURS[c]}
              opacity={0.85}
            />
          );
          xCursor += segW;
          return seg;
        });
        const totalCol = row.sufficient ? '#4ade80' : '#f87171';
        return (
          <g key={row.planet_id}>
            <text
              x={marginL + labelCol - 8}
              y={cyText}
              fontSize={11}
              fontWeight={600}
              fill="#e5e7eb"
              textAnchor="end"
              dominantBaseline="middle"
            >
              {label}
            </text>
            {segments}
            <line
              x1={tx}
              y1={y - 2}
              x2={tx}
              y2={y + barH + 2}
              stroke="#dc2626"
              strokeWidth={1.5}
              strokeDasharray="2 2"
            />
            <text x={xCursor + 4} y={cyText} fontSize={10} fontWeight={700} fill={totalCol} dominantBaseline="middle">
              {row.total_rupas.toFixed(2)}R
            </text>
          </g>
        );
      })}
      {/* Legend */}
      {components.map((c, j) => {
        const lx = marginL + labelCol + j * 90;
        const ly = height - 8;
        return (
          <g key={c}>
            <rect x={lx} y={ly - 7} width={9} height={9} fill={COMPONENT_COLOURS[c]} opacity={0.85} />
            <text x={lx + 13} y={ly} fontSize={8} fill="#a1a1aa">
              {lang === 'hi' ? COMPONENT_LABELS[c].hi : COMPONENT_LABELS[c].en}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ─── 3. Bhāva heatmap ──────────────────────────────────────────────────────

export function BhavaHeatmap({
  rows,
  lang,
  width = 640,
  height = 200,
}: {
  rows: BhavaBalaRow[];
  lang: 'hi' | 'en';
  width?: number;
  height?: number;
}) {
  if (!rows.length) return null;
  const marginL = 90;
  const marginT = 30;
  const marginR = 12;
  const marginB = 50;
  const cellsW = width - marginL - marginR;
  const cellsH = height - marginT - marginB;
  const cellW = cellsW / 12;
  const cellH = cellsH / 3;
  const components = ['bhavadhipati', 'bhava_dig', 'bhava_drishti'] as const;
  const labels: Record<string, { hi: string; en: string }> = {
    bhavadhipati: { hi: 'भावाधिपति', en: 'Bhāvādhipati' },
    bhava_dig: { hi: 'भाव-दिग्', en: 'Bhāva-dig' },
    bhava_drishti: { hi: 'भाव-दृष्टि', en: 'Bhāva-dṛṣṭi' },
  };
  const allValues: number[] = [];
  rows.forEach((r) => {
    components.forEach((c) => allValues.push(r[c]));
  });
  const vmax = Math.max(60, ...allValues);
  const colour = (v: number): string => {
    const t = Math.max(0, Math.min(1, v / vmax));
    const r = Math.round(0xcb + (0xb4 - 0xcb) * t);
    const g = Math.round(0xd5 + (0x53 - 0xd5) * t);
    const b = Math.round(0xe1 + (0x09 - 0xe1) * t);
    return `rgb(${r}, ${g}, ${b})`;
  };

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full">
      {components.map((c, i) => {
        const y = marginT + i * cellH + cellH / 2;
        return (
          <text
            key={c}
            x={marginL - 6}
            y={y}
            fontSize={9.5}
            fill="#e5e7eb"
            textAnchor="end"
            dominantBaseline="middle"
          >
            {lang === 'hi' ? labels[c].hi : labels[c].en}
          </text>
        );
      })}
      {rows.map((row, hIdx) => (
        <g key={hIdx}>
          {components.map((c, cIdx) => {
            const v = row[c];
            const x = marginL + hIdx * cellW;
            const y = marginT + cIdx * cellH;
            const tColor = v < vmax * 0.55 ? '#1e293b' : '#fef3c7';
            return (
              <g key={c}>
                <rect x={x} y={y} width={cellW - 1} height={cellH - 1} fill={colour(v)} />
                <text
                  x={x + cellW / 2}
                  y={y + cellH / 2}
                  fontSize={8}
                  fill={tColor}
                  textAnchor="middle"
                  dominantBaseline="middle"
                >
                  {Math.round(v)}
                </text>
              </g>
            );
          })}
        </g>
      ))}
      {rows.map((row, hIdx) => {
        const x = marginL + hIdx * cellW + cellW / 2;
        const y = marginT + cellsH + 14;
        return (
          <g key={hIdx}>
            <text x={x} y={y} fontSize={9} fontWeight={600} fill="#e5e7eb" textAnchor="middle">
              {hIdx + 1}
            </text>
            <text x={x} y={y + 12} fontSize={8} fill="#a1a1aa" textAnchor="middle">
              {row.total_rupas.toFixed(1)}R
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ─── 4. Iṣṭa / Kaṣṭa diverging bar ────────────────────────────────────────

export function IshtaKashtaBar({
  rows,
  lang,
  width = 540,
  height = 320,
}: {
  rows: ShadbalaFullRow[];
  lang: 'hi' | 'en';
  width?: number;
  height?: number;
}) {
  if (!rows.length) return null;
  const marginT = 36;
  const marginB = 20;
  const marginL = 60;
  const marginR = 60;
  const labelCol = 55;
  const plotW = width - marginL - marginR - labelCol;
  const halfW = plotW / 2;
  const plotH = height - marginT - marginB;
  const rowH = plotH / rows.length;
  const rowGap = 6;
  const barH = rowH - rowGap;
  const cx = marginL + labelCol + halfW;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full">
      <text x={cx - halfW / 2} y={26} fontSize={9} fontWeight={600} fill="#f87171" textAnchor="middle">
        {lang === 'hi' ? '← कष्ट' : '← Kaṣṭa'}
      </text>
      <text x={cx + halfW / 2} y={26} fontSize={9} fontWeight={600} fill="#4ade80" textAnchor="middle">
        {lang === 'hi' ? 'इष्ट →' : 'Iṣṭa →'}
      </text>
      <line x1={cx} y1={marginT} x2={cx} y2={marginT + plotH} stroke="#52525b" strokeWidth={0.8} />
      {rows.map((row, i) => {
        const y = marginT + i * rowH + rowGap / 2;
        const cyText = y + barH / 2;
        const ishtaW = (row.ishta / 60) * halfW;
        const kashtaW = (row.kashta / 60) * halfW;
        const label = lang === 'hi' ? PLANET_HI[row.planet_id] : PLANET_EN[row.planet_id];
        return (
          <g key={row.planet_id}>
            <rect x={cx - kashtaW} y={y} width={kashtaW} height={barH} fill="#dc2626" opacity={0.78} />
            <rect x={cx} y={y} width={ishtaW} height={barH} fill="#16a34a" opacity={0.78} />
            <text
              x={marginL + labelCol - 8}
              y={cyText}
              fontSize={11}
              fontWeight={600}
              fill="#e5e7eb"
              textAnchor="end"
              dominantBaseline="middle"
            >
              {label}
            </text>
            <text
              x={cx - kashtaW - 4}
              y={cyText}
              fontSize={8.5}
              fill="#fca5a5"
              textAnchor="end"
              fontWeight={600}
              dominantBaseline="middle"
            >
              {row.kashta.toFixed(1)}
            </text>
            <text
              x={cx + ishtaW + 4}
              y={cyText}
              fontSize={8.5}
              fill="#86efac"
              textAnchor="start"
              fontWeight={600}
              dominantBaseline="middle"
            >
              {row.ishta.toFixed(1)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
